import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, fmtDate, hasPerm } from "@/lib/helpers";
import { StudentForm } from "@/pages/Students";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";
import {
  TrendingUp, TrendingDown, Users2, IdCard, Cake, AlertCircle, Bus,
  UserPlus, HandCoins, Wallet, CalendarCheck, PhoneCall, FileBarChart, Loader2, CalendarDays, SlidersHorizontal, Megaphone, NotebookPen, ChevronUp, ChevronDown, ClipboardList, BookOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { orderWidgets } from "@/lib/dashboardWidgets";
import SuperAdminDashboard from "@/pages/SuperAdminDashboard";
import { useSAMode } from "@/lib/saMode";
import { toast } from "sonner";

const LinkSub = ({ onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className="text-xs text-indigo-600 hover:underline font-medium mt-1">{children} →</button>
);

const KPI = ({ label, value, sub, tone = "slate", icon: Icon, style }) => {
  const tones = {
    indigo: "text-indigo-600 bg-indigo-50",
    green: "text-emerald-600 bg-emerald-50",
    rose: "text-rose-600 bg-rose-50",
    amber: "text-amber-600 bg-amber-50",
    slate: "text-slate-600 bg-slate-100",
  };
  return (
    <div style={style} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
          <div className="text-2xl font-heading font-extrabold text-slate-900 mt-1 tabular">{value}</div>
          {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
        </div>
        {Icon && <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon size={18} /></div>}
      </div>
    </div>
  );
};

const QuickAction = ({ label, icon: Icon, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="flex flex-col items-center justify-center gap-2 h-24 rounded-xl bg-white border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 hover:-translate-y-0.5 transition-all">
    <div className="w-10 h-10 rounded-lg bg-indigo-600 text-white flex items-center justify-center"><Icon size={20} /></div>
    <span className="text-sm font-medium text-slate-700">{label}</span>
  </button>
);

const MiniBar = ({ title, data, testid }) => (
  <div className="flex-1 min-w-[260px]" data-testid={testid}>
    <div className="text-xs font-semibold text-slate-500 mb-2">{title}</div>
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
        <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={0} />
        <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} />
        <Tooltip />
        <Bar dataKey="present" name="Present" fill="#10b981" radius={[4, 4, 0, 0]} />
        <Bar dataKey="absent" name="Absent" fill="#f43f5e" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

function AttendanceCharts() {
  const [ov, setOv] = useState(null);
  useEffect(() => { api.get("/attendance/overview").then((r) => setOv(r.data)).catch(() => {}); }, []);
  if (!ov) return null;
  const daily = [{ label: "Present", present: ov.daily.present, absent: 0 }, { label: "Absent", present: 0, absent: ov.daily.absent }];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="attendance-charts-widget">
      <h3 className="font-heading font-bold text-slate-900 mb-4">Student Attendance</h3>
      <div className="flex flex-wrap gap-6">
        <MiniBar title="Yearly (Apr–Mar)" data={ov.yearly} testid="att-chart-yearly" />
        <MiniBar title="Monthly (this month)" data={ov.monthly} testid="att-chart-monthly" />
        <MiniBar title={`Daily (${ov.daily.date})`} data={daily} testid="att-chart-daily" />
      </div>
    </div>
  );
}

function SchoolDashboard() {
  const [d, setD] = useState(null);
  const [vload, setVload] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [studentFormOpen, setStudentFormOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [settings, setSettings] = useState(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [savingWidgets, setSavingWidgets] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
    api.get("/settings").then((r) => { setSettings(r.data || {}); setHidden(r.data?.dashboard_hidden || []); }).catch(() => {});
    api.get("/holidays/upcoming", { params: { limit: 5 } }).then((r) => setHolidays(r.data || [])).catch(() => {});
    if (hasPerm(user, "add_student")) {
      api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
    }
    if (hasPerm(user, "view_transport")) api.get("/transport/vehicle-load").then((r) => setVload(r.data)).catch(() => {});
  }, []);

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  const show = (k) => !hidden.includes(k);
  const f = d.financial;
  const refreshDashboard = () => {
    api.get("/dashboard").then((r) => setD(r.data));
  };
  const toggleWidget = async (key) => {
    if (!settings) return;
    const prev = hidden;
    const newHidden = hidden.includes(key) ? hidden.filter((k) => k !== key) : [...hidden, key];
    setHidden(newHidden);
    setSavingWidgets(true);
    try {
      await api.put("/settings", { ...settings, dashboard_hidden: newHidden });
      setSettings((s) => ({ ...s, dashboard_hidden: newHidden }));
    } catch (e) { toast.error("Could not save widget settings"); setHidden(prev); }
    finally { setSavingWidgets(false); }
  };
  const orderedWidgets = orderWidgets(settings?.dashboard_order);
  const orderIndex = Object.fromEntries(orderedWidgets.map((w, i) => [w.key, i]));
  const moveWidget = async (key, dir) => {
    if (!settings) return;
    const keys = orderedWidgets.map((w) => w.key);
    const i = keys.indexOf(key);
    const j = dir === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= keys.length) return;
    [keys[i], keys[j]] = [keys[j], keys[i]];
    const prevSettings = settings;
    setSettings((s) => ({ ...s, dashboard_order: keys }));
    setSavingWidgets(true);
    try {
      await api.put("/settings", { ...settings, dashboard_order: keys });
    } catch (e) { toast.error("Could not save widget order"); setSettings(prevSettings); }
    finally { setSavingWidgets(false); }
  };
  const actions = [
    { label: "Collect Fees", icon: HandCoins, to: "/due-fees", perm: "collect_fee", wkey: "qa_collect_fees", testid: "qa-collect-fees" },
    {
      label: "Add Student",
      icon: UserPlus,
      perm: "add_student",
      wkey: "qa_add_student",
      testid: "qa-add-student",
      onClick: () => setStudentFormOpen(true),
    },
    { label: "Add Expense", icon: Wallet, to: "/expenses", perm: "add_expense", wkey: "qa_add_expense", testid: "qa-add-expense" },
    { label: "Mark Attendance", icon: CalendarCheck, to: "/attendance", perm: "edit_attendance", wkey: "qa_mark_attendance", testid: "qa-attendance" },
    { label: "Marks Entry", icon: ClipboardList, to: "/exams/marks", perm: "enter_marks", wkey: "qa_marks_entry", testid: "qa-marks-entry" },
    { label: "Homework", icon: BookOpen, to: "/homework", perm: "view_homework", wkey: "qa_homework", testid: "qa-homework" },
    { label: "Fee Follow-up", icon: PhoneCall, to: "/followup", perm: "view_followup", wkey: "qa_followup", testid: "qa-followup" },
    { label: "Due Fees", icon: AlertCircle, to: "/due-fees", perm: "view_fees", wkey: "qa_due_fees", testid: "qa-due-fees" },
    { label: "Reports", icon: FileBarChart, to: "/reports", perm: "view_reports", wkey: "qa_reports", testid: "qa-reports" },
  ].filter((a) => hasPerm(user, a.perm) && show(a.wkey)).sort((a, b) => (orderIndex[a.wkey] ?? 0) - (orderIndex[b.wkey] ?? 0));

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">Dashboard</h1>
          <p className="text-slate-500">Welcome back, {user?.name}. Here's your school at a glance.</p>
        </div>
        {hasPerm(user, "manage_settings") && (
          <Button variant="outline" className="gap-1.5" onClick={() => setCustomizeOpen(true)} data-testid="customize-widgets-btn"><SlidersHorizontal size={16} />Customize Widgets</Button>
        )}
      </div>

      <Sheet open={customizeOpen} onOpenChange={setCustomizeOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="customize-widgets-sheet">
          <SheetHeader>
            <SheetTitle>Customize Widgets</SheetTitle>
            <SheetDescription>Toggle what appears on your Dashboard. Changes save instantly and hidden items reflow automatically.</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-1">
            {orderedWidgets.map((w, idx) => (
              <div key={w.key} className="flex items-center justify-between gap-3 py-2.5 px-3 rounded-lg border border-slate-100 hover:bg-slate-50" data-testid={`customize-widget-${w.key}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex flex-col -space-y-1 shrink-0">
                    <button type="button" aria-label="Move up" title="Move up" disabled={idx === 0 || savingWidgets} onClick={() => moveWidget(w.key, "up")} className="text-slate-400 hover:text-indigo-600 disabled:opacity-30" data-testid={`customize-up-${w.key}`}><ChevronUp size={15} /></button>
                    <button type="button" aria-label="Move down" title="Move down" disabled={idx === orderedWidgets.length - 1 || savingWidgets} onClick={() => moveWidget(w.key, "down")} className="text-slate-400 hover:text-indigo-600 disabled:opacity-30" data-testid={`customize-down-${w.key}`}><ChevronDown size={15} /></button>
                  </div>
                  <span className="text-sm text-slate-700 truncate">{w.label}</span>
                </div>
                <Switch checked={!hidden.includes(w.key)} onCheckedChange={() => toggleWidget(w.key)} disabled={savingWidgets} data-testid={`customize-toggle-${w.key}`} />
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {show("quick_actions") && actions.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-3">Quick Actions</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            {actions.map((a) => (
              <QuickAction
                key={a.wkey}
                label={a.label}
                icon={a.icon}
                testid={a.testid}
                onClick={a.onClick || (() => nav(a.to))}
              />
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {show("kpi_total_fees") && <KPI style={{ order: orderIndex.kpi_total_fees }} label="Total Fees" value={fmt(f.total_fees)} tone="indigo" icon={TrendingUp}
          sub={hasPerm(user, "view_students") && <LinkSub onClick={() => nav("/students")} testid="kpi-total-students-link">{d.notifications.total_students} Total Students</LinkSub>} />}
        {show("kpi_paid_fees") && <KPI style={{ order: orderIndex.kpi_paid_fees }} label="Paid Fees" value={fmt(f.paid_fees)} tone="green" icon={TrendingUp}
          sub={hasPerm(user, "view_students") && <LinkSub onClick={() => nav("/students")} testid="kpi-paid-students-link">{d.notifications.paid_fees_count} Students Paid</LinkSub>} />}
        {show("kpi_due_fees") && <KPI style={{ order: orderIndex.kpi_due_fees }} label="Due Fees" value={fmt(f.due_fees)} tone="rose" icon={AlertCircle}
          sub={hasPerm(user, "view_fees") && <LinkSub onClick={() => nav("/due-fees")} testid="kpi-pending-students-link">{d.notifications.pending_fees_count} Students Pending</LinkSub>} />}
        {show("kpi_total_discount") && <KPI style={{ order: orderIndex.kpi_total_discount }} label="Total Discount" value={fmt(f.total_discount)} tone="amber" icon={TrendingDown} />}
        {show("kpi_today_collection") && <KPI style={{ order: orderIndex.kpi_today_collection }} label="Today Collection" value={fmt(f.today_collection)} tone="green" icon={HandCoins} />}
        {show("kpi_today_expenses") && <KPI style={{ order: orderIndex.kpi_today_expenses }} label="Today Expenses" value={fmt(f.today_expense)} tone="rose" icon={Wallet} />}
        {show("kpi_month_collection") && <KPI style={{ order: orderIndex.kpi_month_collection }} label="Month Collection" value={fmt(f.month_collection)} tone="green" icon={TrendingUp} />}
        {show("kpi_month_expenses") && <KPI style={{ order: orderIndex.kpi_month_expenses }} label="Month Expenses" value={fmt(f.month_expense)} tone="rose" icon={TrendingDown} />}
        {show("kpi_homework_today") && <KPI style={{ order: orderIndex.kpi_homework_today }} label="Homework Today" value={d.notifications.homework_today ?? 0} tone="indigo" icon={NotebookPen}
          sub={hasPerm(user, "view_homework") && <LinkSub onClick={() => nav("/homework")} testid="kpi-homework-today-link">View Homework</LinkSub>} />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 grid-flow-row-dense">
        {show("fee_chart") && (
        <div style={{ order: orderIndex.fee_chart }} className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Fee Collection — Apr to Mar</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.chart}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Bar dataKey="amount" fill="#4F46E5" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        )}

          {show("attendance_charts") && hasPerm(user, "view_attendance") && (
            <div style={{ order: orderIndex.attendance_charts }} className="lg:col-span-3"><AttendanceCharts /></div>
          )}

          {show("transport_panel") && hasPerm(user, "view_transport") && (
            <div style={{ order: orderIndex.transport_panel }} className="bg-white rounded-xl border border-slate-200 p-5" data-testid="transport-panel">
              <div className="flex items-center gap-2 mb-4">
                <Bus size={16} className="text-indigo-600" /><span className="font-semibold text-slate-800">Transport</span>
                <button onClick={() => nav("/transport")} className="ml-auto text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-2.5 py-1" data-testid="new-transport-btn">New Transport</button>
              </div>
              {vload.length === 0 ? <p className="text-xs text-slate-400">No vehicles yet.</p> : (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {vload.map((v) => (
                    <div key={v.id} className="flex items-center justify-between text-sm gap-2" data-testid={`vload-${v.vehicle_number}`}>
                      <span className="text-slate-700 truncate">{v.vehicle_name ? `${v.vehicle_name} · ${v.vehicle_number}` : v.vehicle_number} <span className="text-xs text-slate-400">({v.vehicle_type})</span></span>
                      <span className="flex items-center gap-3 shrink-0">
                        <span className="font-semibold tabular text-indigo-600">{v.students} student{v.students === 1 ? "" : "s"}</span>
                        <span className="font-semibold tabular text-emerald-600 min-w-[64px] text-right" data-testid={`vload-fees-${v.vehicle_number}`}>{fmt(v.total_fees || 0)}</span>
                      </span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between text-sm gap-2 border-t border-slate-200 pt-1.5 mt-1" data-testid="vload-total">
                    <span className="font-bold text-slate-800">Total</span>
                    <span className="flex items-center gap-3 shrink-0">
                      <span className="font-bold tabular text-indigo-700" data-testid="vload-total-students">{vload.reduce((s, v) => s + (v.students || 0), 0)} student{vload.reduce((s, v) => s + (v.students || 0), 0) === 1 ? "" : "s"}</span>
                      <span className="font-bold tabular text-emerald-700 min-w-[64px] text-right" data-testid="vload-total-fees">{fmt(vload.reduce((s, v) => s + (v.total_fees || 0), 0))}</span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
          {show("students_panel") && (
          <div style={{ order: orderIndex.students_panel }} className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3"><Users2 size={16} className="text-indigo-600" /><span className="font-semibold text-slate-800">Students</span>{d.students.date && <span className="text-xs text-slate-400 ml-auto" data-testid="student-att-date">as of {d.students.date}</span>}</div>
            <div className="grid grid-cols-3 text-center">
              <div><div className="text-xl font-bold tabular">{d.students.total}</div><div className="text-xs text-slate-400">Total</div></div>
              <div><div className="text-xl font-bold text-emerald-600 tabular">{d.students.present}</div><div className="text-xs text-slate-400">Present</div></div>
              <div><div className="text-xl font-bold text-rose-600 tabular">{d.students.absent}</div><div className="text-xs text-slate-400">Absent</div></div>
            </div>
          </div>
          )}
          {show("staff_panel") && (
          <div style={{ order: orderIndex.staff_panel }} className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3"><IdCard size={16} className="text-indigo-600" /><span className="font-semibold text-slate-800">Staff</span>{d.staff.date && <span className="text-xs text-slate-400 ml-auto" data-testid="staff-att-date">as of {d.staff.date}</span>}</div>
            <div className="grid grid-cols-3 text-center">
              <div><div className="text-xl font-bold tabular">{d.staff.total}</div><div className="text-xs text-slate-400">Total</div></div>
              <div><div className="text-xl font-bold text-emerald-600 tabular">{d.staff.present}</div><div className="text-xs text-slate-400">Present</div></div>
              <div><div className="text-xl font-bold text-rose-600 tabular">{d.staff.absent}</div><div className="text-xs text-slate-400">Absent</div></div>
            </div>
          </div>
          )}
          {show("upcoming_holidays") && (
          <div style={{ order: orderIndex.upcoming_holidays }} className="bg-white rounded-xl border border-slate-200 p-5" data-testid="upcoming-holidays-panel">
            <div className="flex items-center gap-2 mb-3"><CalendarDays size={16} className="text-violet-600" /><span className="font-semibold text-slate-800">Upcoming Holidays</span></div>
            {holidays.length === 0 ? <p className="text-xs text-slate-400">No upcoming holidays scheduled.</p> : (
              <div className="space-y-1.5 max-h-44 overflow-y-auto">
                {holidays.map((h) => {
                  const days = Math.round((new Date(`${h.date}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);
                  return (
                    <div key={h.date} className="flex items-center justify-between text-sm p-2 rounded-lg hover:bg-slate-50 gap-2" data-testid={`upcoming-holiday-${h.date}`}>
                      <div className="min-w-0"><div className="font-medium text-slate-800 truncate">{h.name}</div><div className="text-xs text-slate-400">{fmtDate(h.date)}</div></div>
                      <span className={`text-xs font-semibold shrink-0 ${days <= 0 ? "text-violet-600" : "text-slate-400"}`}>{days <= 0 ? "Today" : `in ${days} day${days > 1 ? "s" : ""}`}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}
        {show("birthdays") && (
        <div style={{ order: orderIndex.birthdays }} className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-3"><Cake size={18} className="text-pink-500" /><h3 className="font-heading font-bold text-slate-900">Birthdays</h3></div>
          {d.notifications.today_birthdays.length === 0 && d.notifications.upcoming_birthdays.length === 0 ? (
            <p className="text-sm text-slate-400">No birthdays today or this week.</p>
          ) : (
            <div className="space-y-2">
              {d.notifications.today_birthdays.map((b, i) => (
                <div key={i} className="flex items-center justify-between text-sm p-2 rounded-lg bg-pink-50">
                  <span className="font-medium text-slate-800">🎂 {b.name}</span>
                  <span className="text-pink-600 font-semibold">Today!</span>
                </div>
              ))}
              {d.notifications.upcoming_birthdays.map((b, i) => (
                <div key={i} className="flex items-center justify-between text-sm p-2 rounded-lg hover:bg-slate-50">
                  <span className="text-slate-700">{b.name}</span>
                  <span className="text-slate-400">in {b.days} day{b.days > 1 ? "s" : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
        {show("fee_followup") && (
        <div style={{ order: orderIndex.fee_followup }} className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4"><AlertCircle size={18} className="text-amber-500" /><h3 className="font-heading font-bold text-slate-900">Fee Follow-up</h3></div>
          <p className="text-sm text-slate-600">
            <span className="text-2xl font-bold text-rose-600 tabular">{d.notifications.pending_fees_count}</span> students have pending fees.
          </p>
          {hasPerm(user, "view_fees") && (
            <button onClick={() => nav("/due-fees")} className="mt-3 text-sm text-indigo-600 font-medium hover:underline" data-testid="view-due-link">View Due Fees →</button>
          )}
        </div>
        )}
        {show("leads_panel") && hasPerm(user, "view_leads") && d.leads && (
        <div style={{ order: orderIndex.leads_panel }} className="bg-white rounded-xl border border-slate-200 p-6" data-testid="dashboard-leads-widget">
          <div className="flex items-center gap-2 mb-4">
            <Megaphone size={18} className="text-indigo-500" />
            <h3 className="font-heading font-bold text-slate-900">Leads / Inquiry</h3>
            <span className="ml-auto text-xs text-slate-400" data-testid="dashboard-leads-total">{d.leads.total} total</span>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {[["New", d.leads.new, "text-blue-600 bg-blue-50"], ["Contacted", d.leads.contacted, "text-amber-600 bg-amber-50"], ["Visited", d.leads.visited, "text-violet-600 bg-violet-50"], ["Admitted", d.leads.admitted, "text-emerald-600 bg-emerald-50"], ["Lost", d.leads.lost, "text-rose-600 bg-rose-50"], ["Due F/U", d.leads.followups_due, "text-slate-700 bg-slate-100"]].map(([lbl, val, cls]) => (
              <div key={lbl} className={`rounded-lg p-2 text-center ${cls}`}>
                <div className="text-xl font-extrabold tabular">{val}</div>
                <div className="text-[10px] font-semibold uppercase tracking-wide">{lbl}</div>
              </div>
            ))}
          </div>
          <button onClick={() => nav("/leads")} className="text-sm text-indigo-600 font-medium hover:underline" data-testid="dashboard-leads-link">Open Leads / Inquiry →</button>
        </div>
        )}
      </div>
      <StudentForm
        open={studentFormOpen}
        onOpenChange={setStudentFormOpen}
        classes={classes}
        edit={null}
        onSaved={refreshDashboard}
      />
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const saMode = useSAMode();
  if (user?.role === "super_admin" && saMode === "super") return <SuperAdminDashboard />;
  return <SchoolDashboard />;
}
